import { SkidBattery } from "./battery/SkidBattery";
import { Location, ILocation } from "./Location";
import { LocationDto, SkidDataDto } from "./LocationDTO";
import { TimestampedObjectMixin } from "src/app/_models/mixins/TimestampedObject";
import { StaticImplements } from "src/app/_helpers/helpers";
import { LoggedUserService } from "src/app/_services/logged-user.service";

class _SkidData {
  id_skid_data: number;
  id_skid: number;
  created_at: Date;
  updated_at: Date;
  grid_setpoint_kw: number;
  battery_mode: string;
  battery_sum_kw: number;
  grid_L1: number;
  grid_L2: number;
  grid_L3: number;
  hypercharger_limit_kw: number;
  hypercharger_conn1_amp: number;
  hypercharger_conn1_soc: number;
  hypercharger_conn1_status: number;
  hypercharger_conn2_amp: number;
  hypercharger_conn2_soc: number;
  hypercharger_conn2_status: number;
  hypercharger_s_kva: number;
  hypercharger_q_kvar: number;
  hypercharger_p_kw: number;
  ems_remote_req: number;
  ems_remote_effective: number;
  ems_block_reason: number;
  ems_kw_set_req: number;
  ems_kw_set_eff: number;
  ems_min_cap_kwh: number;
  ems_cap_kwh: number;
  ems_deadline_min: number;
  ems_deadline_min_remaining: number;
  ems_missing_kwh: number;
  ems_required_avg_kw: number;
  ems_force_charge: number;
  ems_plan_mode: number;
  grid_P: number;
  grid_S: number;
  grid_Q: number;

  spot_level_qh: number;

  static fromDto(dto: SkidDataDto): SkidData {
    const data = new SkidData();
    Object.assign(data, dto);
    data.created_at = new Date(dto.created_at);
    data.updated_at = new Date(dto.updated_at);
    return data;
  }

  /** `true` when hypercharger connector 1 has an active session. */
  get conn1Active(): boolean {
    return this.hypercharger_conn1_status > 0;
  }

  /** `true` when hypercharger connector 2 has an active session. */
  get conn2Active(): boolean {
    return this.hypercharger_conn2_status > 0;
  }

  /**
   * Whether the device is reporting healthily — the freshness/health flag the
   * shared `FleetDevice` contract expects on `lastData`. Anything other than an
   * explicit `OFFLINE`/`ERROR` mode counts as ok (staleness is handled
   * separately by the shell via `is_old`).
   */
  get ok(): boolean {
    const mode = this.battery_mode?.toUpperCase();
    return mode !== "OFFLINE" && mode !== "ERROR";
  }

  /** Colour-accent keyword for the capacity ring, derived from `battery_mode`. */
  get ringState(): string {
    switch (this.battery_mode?.toUpperCase()) {
      case "CHARGING":
        return "charging";
      case "DISCHARGING":
        return "discharging";
      case "IDLE":
      case "STANDBY":
        return "standby";
      case "OFFLINE":
        return "offline";
      case "ERROR":
        return "error";
      default:
        return "standby";
    }
  }

  /** Bootstrap background class reflecting the current `battery_mode`. */
  get statusClass(): string {
    switch (this.battery_mode?.toUpperCase()) {
      case "CHARGING":
        return "bg-success";
      case "DISCHARGING":
        return "bg-info";
      case "IDLE":
      case "STANDBY":
        return "bg-warning text-black";
      case "OFFLINE":
        return "bg-grey text-black";
      case "ERROR":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  }

  /** Human-readable label for `ems_plan_mode` (`"OFF"` / `"NORMAL"` / `"FORCED"`). */
  get planModeLabel(): string {
    switch (this.ems_plan_mode) {
      case 0:
        return "OFF";
      case 1:
        return "NORMAL";
      case 2:
        return "FORCED";
      default:
        return `${this.ems_plan_mode}`;
    }
  }

  /** Bootstrap background class for `ems_plan_mode`. */
  get planModeClass(): string {
    switch (this.ems_plan_mode) {
      case 0:
        return "bg-secondary";
      case 1:
        return "bg-success";
      case 2:
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  }

  /** `ems_deadline_min` formatted as `HH:MM`; returns `"—"` when null. */
  get deadlineHHMM(): string {
    if (this.ems_deadline_min == null) return "—";
    const h = Math.floor(this.ems_deadline_min / 60);
    const m = this.ems_deadline_min % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  }
}

/** One operational snapshot from a Skid device, with age helpers from the mixin. */
export class SkidData extends TimestampedObjectMixin(_SkidData) {}

/**
 * Location type representing a physical Skid cabinet with integrated battery storage
 * and optional hypercharger connectors.
 */
export class Skid extends Location {
  public static FromDto(dto: LocationDto): Skid {
    const skid = Location.fromDto(new Skid(), dto);
    skid.batteries = dto.batteries?.map((b) => SkidBattery.fromDto(b)) ?? [];

    // Partial DB dumps (and the shallow `GET /location` list) can omit the
    // related `skid` row. Degrade gracefully instead of throwing — a throw here
    // would abort parsing of every other location and cascade error reports.
    if (!dto.skid) {
      console.warn("Skid.FromDto: missing skid relation for location", dto);
      return skid;
    }

    skid.id_skid = dto.skid.id_skid;
    skid.hash = dto.skid.hash;
    skid.ip = dto.skid.ip;
    skid.serial_number = dto.skid.serial_number;
    skid.sim_card_number = dto.skid.sim_card_number;
    skid.max_capacity = dto.skid.max_capacity;
    skid.chargers_count = dto.skid.chargers_count;

    skid.skid_data = dto.skid.skid_data?.map((d) => SkidData.fromDto(d)) ?? [];
    skid.skid_data_latest = dto.skid.skid_data_latest
      ? SkidData.fromDto(dto.skid.skid_data_latest)
      : null;

    return skid;
  }

  public FromDto(dto: LocationDto): ILocation {
    return Skid.FromDto(dto);
  }

  /** Alias for `skid_data_latest`. */
  get lastData(): SkidData | undefined {
    return this.skid_data_latest;
  }

  /** Sum of `power_w` across all batteries in watts. */
  get batterySum(): number {
    return this.batteries.reduce((sum, b) => {
      return sum + (b.battery_data_latest?.power_w ?? 0);
    }, 0);
  }

  /** Signed total battery power in kW (+ charging / − discharging convention as reported). */
  get batterySumKw(): number {
    return this.batterySum / 1000;
  }

  /** Average state-of-charge across all batteries (%); `0` when none. */
  get avgSoc(): number {
    if (!this.batteries?.length) {
      return 0;
    }
    return (
      this.batteries.reduce((sum, b) => {
        return sum + (b.battery_data_latest?.soc ?? 0);
      }, 0) / this.batteries.length
    );
  }

  /** `true` when the latest snapshot reports `battery_mode === "ERROR"`. */
  get hasErrors(): boolean {
    return this.lastData?.battery_mode?.toUpperCase() === "ERROR";
  }

  /**
   * How many sub-batteries currently report a BMS/PCS alarm. Drives the red
   * ERROR pill + accent on the fleet card/row, the detail header and the
   * dashboard hero. Alarm detail is an operational error users restricted to
   * a limited view (ViewRestricted) must not see, so for them this always
   * reads 0 (see `alarmingBatteries`).
   */
  get alarmCount(): number {
    return this.alarmingBatteries.length;
  }

  /**
   * The sub-batteries currently reporting a BMS/PCS alarm — the ones the
   * dashboard banner and the sub-battery table highlight in red. Empty for
   * ViewRestricted users: this is the single gate every skid screen relies on,
   * so none of them can leak an alarm to a restricted view.
   */
  get alarmingBatteries(): SkidBattery[] {
    if (LoggedUserService.hasModule("ViewRestricted")) {
      return [];
    }
    return (this.batteries ?? []).filter(
      (b) => b.hasCriticalAlarm || b.hasAlarm,
    );
  }

  id_skid: number;
  /** 30-char URL-safe deterministic hash of the location's name — encoded into the skid's QR code label. */
  hash: string;
  ip: string;
  serial_number: string;
  sim_card_number: string;
  max_capacity: number;
  /** Number of physical charger connectors on this skid. */
  chargers_count: number;

  batteries: SkidBattery[];

  skid_data: SkidData[];
  skid_data_latest?: SkidData;
}
